// frame: cSjks @ ui/dogdisk-user.pen
// spec: docs/ui/user-page-flows.md §2.4 cSjks 页面-资源包回收站
//
// 数据源:v_resource_pack_mine 加 status='deleted' filter
// (resource_pack.md §3.1;TODO(api): 当前 view 只暴露 active pack,
// 需要 spec 加 v_resource_pack_recycled_mine 或 status filter)。
// 恢复:POST /gapi/user/packs/{pack_id}/restore(file-management.md §3 已 spec)。
//
// 文案规则与 .pen 对齐:已删除的资源包永久保留,已订阅用户仍可访问。

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient, getSupabase } from '@dogdisk/shared'
import { AppLayout } from '../components/app-layout'
import { MobileScreen, TopBar } from '../components/mobile-screen'
import { resourceUrl } from '../_lib/resource-node-pref'

type PackTrashRow = {
  pack_id: string
  title: string
  cover_url: string | null
  subscriber_count: number
  /** 软删时间;view 暂未投影 → fallback updated_at */
  deleted_at: string | null
}

function usePackTrash() {
  return useQuery({
    queryKey: ['trash', 'packs'] as const,
    queryFn: async (): Promise<PackTrashRow[]> => {
      const sb = getSupabase()
      // TODO(api): v_resource_pack_mine 当前可能默认过滤 status='active';
      // 实际需要加 ?status=eq.deleted 或独立 view v_resource_pack_recycled_mine。
      const { data, error } = await sb
        .from('v_resource_pack_mine')
        .select('pack_id, title, cover_url, subscriber_count, deleted_at, status')
        .eq('status', 'deleted')
        .order('deleted_at', { ascending: false })
        .limit(200)
      if (error) return []
      return ((data ?? []) as Array<PackTrashRow & { status: string }>).map((r) => ({
        pack_id: r.pack_id,
        title: r.title,
        cover_url: r.cover_url,
        subscriber_count: r.subscriber_count,
        deleted_at: r.deleted_at,
      }))
    },
  })
}

export function PackTrashPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { data: rows = [], isLoading } = usePackTrash()
  const [busyId, setBusyId] = useState<string | null>(null)

  const restoreMut = useMutation({
    mutationFn: (packId: string) =>
      apiClient.post(`/gapi/user/packs/${packId}/restore`, {}),
    onMutate: (id) => setBusyId(id),
    onSettled: () => {
      setBusyId(null)
      void qc.invalidateQueries({ queryKey: ['trash', 'packs'] })
      void qc.invalidateQueries({ queryKey: ['packs', 'mine'] })
    },
  })

  return (
    <AppLayout>
      <MobileScreen bg="surface">
        <div data-testid="cSjks" className="flex flex-1 flex-col">
          <TopBar
            title="资源包回收站"
            fallback="/my-shares"
          />
          <div className="mx-6 mt-2 rounded-xl bg-sky-50 px-3 py-2 text-[12px] text-sky-700">
            已删除的资源包永久保留,已订阅用户仍可访问;可随时恢复
          </div>

          <div className="flex-1 px-6 pb-24 pt-3">
            {isLoading ? (
              <SkeletonRows />
            ) : rows.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-16 text-[13px] text-[#9CA3AF]">
                <p>暂无已删除的资源包</p>
                <button
                  type="button"
                  onClick={() => navigate('/my-shares')}
                  className="mt-2 text-[12px] text-[#1971ca]"
                >
                  返回我的分享
                </button>
              </div>
            ) : (
              <ul className="flex flex-col gap-2">
                {rows.map((p, i) => {
                  // restoreBtn1~4 对应 .pen i4di2/SxWI9/0Pndv/zm9eR
                  const ids = ['i4di2', 'SxWI9', '0Pndv', 'zm9eR']
                  const restoreId = ids[i] ?? `restore-${i}`
                  return (
                    <li
                      key={p.pack_id}
                      className="flex items-center gap-3 rounded-xl bg-white px-3 py-3"
                    >
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gray-100">
                        {p.cover_url ? (
                          <img src={resourceUrl(p.cover_url)} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <span className="text-xl">📦</span>
                        )}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[14px] font-medium text-[#1A1A1A]">
                          {p.title}
                        </p>
                        <p className="text-[11px] text-[#9CA3AF]">
                          {p.subscriber_count} 订阅
                          {p.deleted_at ? ` · 删除于 ${p.deleted_at.slice(0, 10)}` : ''}
                        </p>
                      </div>
                      <button
                        type="button"
                        data-testid={restoreId}
                        disabled={busyId === p.pack_id}
                        onClick={() => restoreMut.mutate(p.pack_id)}
                        className="rounded-md bg-emerald-50 px-3 py-1.5 text-[12px] font-medium text-emerald-700 disabled:opacity-50"
                      >
                        {busyId === p.pack_id ? '恢复中…' : '恢复'}
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </div>
      </MobileScreen>
    </AppLayout>
  )
}

function SkeletonRows() {
  return (
    <ul className="flex flex-col gap-2">
      {[1, 2, 3].map((i) => (
        <li key={i} className="flex items-center gap-3 rounded-xl bg-white px-3 py-3">
          <span className="skeleton h-12 w-12 rounded-lg" />
          <div className="flex-1 space-y-2">
            <span className="skeleton block h-3 w-3/5 rounded" />
            <span className="skeleton block h-2 w-2/5 rounded" />
          </div>
        </li>
      ))}
    </ul>
  )
}
